import React, { useState, useEffect } from 'react';
import { Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { getStatusHistoryBySubmissionId } from '../../services/statusHistory.service';
import { statusOptions } from '../../services/submissions.service';

const StatusHistorySection = ({ submissionId }) => {
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!submissionId) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const data = await getStatusHistoryBySubmissionId(submissionId, 10);
        setHistory(data);
      } catch (err) {
        console.error('Error fetching status history:', err);
        setError('Failed to load status history');
      } finally {
        setIsLoading(false);
      }
    };

    fetchHistory();
  }, [submissionId]);

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusStyle = (status) => {
    const option = statusOptions.find(s => s.id === status);
    if (option) {
      return `${option.bgColor} ${option.textColor}`;
    }
    return 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return (
      <div className="mt-6">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Status History</h3>
        <div className="flex items-center justify-center py-4 text-gray-500">
          <Clock size={16} className="animate-spin mr-2" />
          Loading history...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-6">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Status History</h3>
        <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
          {error}
        </div>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="mt-6">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Status History</h3>
        <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-md">
          No status changes recorded yet.
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <h3 className="text-sm font-medium text-gray-700">
          Status History ({history.length})
        </h3>
        {isExpanded ? (
          <ChevronUp size={16} className="text-gray-500" />
        ) : (
          <ChevronDown size={16} className="text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-3">
          {history.map((record, index) => (
            <div 
              key={record.id} 
              className="bg-gray-50 border border-gray-200 rounded-md p-3"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  {record.previousStatus && (
                    <>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusStyle(record.previousStatus)}`}>
                        {record.previousStatus}
                      </span>
                      <span className="text-gray-400">→</span>
                    </>
                  )}
                  <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusStyle(record.newStatus)}`}>
                    {record.newStatus}
                  </span>
                </div>
                {index === 0 && (
                  <span className="text-xs text-blue-600 font-medium">Latest</span>
                )}
              </div>
              <div className="flex items-center text-xs text-gray-500 space-x-3">
                <span className="flex items-center">
                  <Clock size={12} className="mr-1" />
                  {formatDateTime(record.changedAt)}
                </span>
                {record.changedBy && (
                  <span>by {record.changedBy}</span>
                )}
              </div>
              {record.reason && (
                <div className="mt-2 text-xs text-gray-600 bg-white p-2 rounded border">
                  <strong>Reason:</strong> {record.reason}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StatusHistorySection;
